import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";

const stats = [
  { value: "9+", label: "合作影院" },
  { value: "6", label: "覆盖城市" },
  { value: "12", label: "在映影片" },
  { value: "365", label: "每日更新" },
];

const milestones = [
  {
    year: "2019",
    title: "九七电影院上线",
    desc: "以「让找电影更简单」为初心，汇集本地影院排片信息，服务第一批影迷用户。",
  },
  {
    year: "2022",
    title: "覆盖多城市",
    desc: "合作影院拓展至北京、上海、广州等核心城市，排片数据实现每日同步更新。",
  },
  {
    year: "2024",
    title: "上线影讯专题",
    desc: "推出专业影评与行业观察栏目，从「看排片」走向「懂电影」。",
  },
  {
    year: "2026",
    title: "全端体验升级",
    desc: "完成移动端适配与多维度筛选能力升级，让观影决策更高效。",
  },
];

const partners = [
  "九七国际影城",
  "万达影城",
  "CGV 影城",
  "保利国际影城",
  "横店影视城",
];

export default function AboutPage() {
  return (
    <SiteLayout>
      {/* 品牌头部 */}
      <section className="bg-primary-950 pb-12 pt-12 md:pb-16 md:pt-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            关于九七电影院
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-primary-200 md:text-base">
            九七电影院是一个聚焦最新电影排片与影院资讯的信息门户。我们希望用清晰、可靠、好看的方式，
            把「今天看什么、去哪里看」这件事变得简单。
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label} className="rounded-lg border border-primary-800 bg-primary-900 p-4">
                <p className="font-heading text-2xl font-bold text-accent-400 md:text-3xl">
                  {item.value}
                </p>
                <p className="mt-1 text-xs text-primary-200">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 网站介绍与发展目标 */}
      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              我们是谁
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              我们是一支热爱电影的团队。每天，我们整理最新上映影片的资料、收集各城市合作影院的排片与放映时间，
              并用清晰的结构呈现给每一位观众。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              无论是想快速查询某部电影的排片，还是想在城市里找到一家合适的影院，九七电影院都希望成为你最顺手的那一站。
              <strong className="text-foreground-800"> 本站为信息演示站点，购票信息仅供参考，不提供实时在线支付。</strong>
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-eye-line text-lg"></i>
                </span>
                <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-950">我们的愿景</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">
                  成为影迷最信赖的观影信息入口，让每一次观影都省心省力。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-flag-line text-lg"></i>
                </span>
                <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-950">发展目标</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">
                  持续扩展合作影院与城市覆盖，让排片信息更新更快、更准。
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              发展历程
            </h2>
            <ol className="mt-6 space-y-6 border-l border-background-300 pl-6">
              {milestones.map((item) => (
                <li key={item.year} className="relative">
                  <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-accent-500 bg-background-50"></span>
                  <p className="font-heading text-sm font-semibold text-accent-600">{item.year}</p>
                  <h3 className="mt-1 font-heading text-base font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* 联系方式 */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            联系方式
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            如有合作洽谈或内容建议，欢迎通过以下方式与我们联系。
          </p>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">客服热线</h3>
                  <p className="mt-1 text-sm text-foreground-600">400-977-9700（每日 9:00 - 21:00）</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">电子邮箱</h3>
                  <p className="mt-1 text-sm text-foreground-600">service@jiuqi-cinema.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">公司地址</h3>
                  <p className="mt-1 text-sm text-foreground-600">
                    北京市朝阳区三里屯路 19 号院九七大厦 8 层
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <iframe
                title="九七电影院办公地址地图"
                src="https://www.google.com/maps?q=Sanlitun+Beijing&output=embed"
                className="h-[320px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* 合作伙伴 */}
      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          合作伙伴与版权声明
        </h2>
        <p className="mt-2 text-sm text-foreground-600">
          感谢以下合作伙伴为本站提供的影院与排片信息支持。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {partners.map((p) => (
            <span
              key={p}
              className="rounded-lg border border-background-200 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-700"
            >
              {p}
            </span>
          ))}
        </div>
        <p className="mt-6 max-w-3xl text-xs leading-relaxed text-foreground-400">
          © 2026 九七电影院 Jiuqi Cinema. 保留所有权利。本站所展示的影片海报、影院信息均为演示示例，
          相关商标与版权归其各自权利人所有。本站不提供实时在线购票服务，购票请以影院官方渠道为准。
        </p>
        <Link
          to="/movies"
          className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          开始浏览电影
          <i className="ri-arrow-right-line"></i>
        </Link>
      </section>
    </SiteLayout>
  );
}